import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { BUSINESS, SERVICES, LOCATIONS } from "../data/site";

export const Footer = () => (
  <footer data-testid="footer" className="bg-deep border-t border-white/10 relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-28 md:pb-10">
      <div className="grid md:grid-cols-12 gap-12 md:gap-8">
        <div className="md:col-span-4">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-volt mb-5">Visit / Call</p>
          <address className="not-italic space-y-4">
            <a
              data-testid="footer-address-link"
              href={BUSINESS.mapsPlace}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 text-slate-300 hover:text-volt transition-colors duration-300 group"
            >
              <MapPin className="w-5 h-5 text-volt shrink-0 mt-0.5" />
              <span className="leading-relaxed">
                {BUSINESS.name}
                <br />
                {BUSINESS.street}
                <br />
                {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
              </span>
            </a>
            <a
              data-testid="footer-phone-link"
              href={BUSINESS.phoneHref}
              className="flex items-center gap-3 font-display font-bold text-xl text-white hover:text-volt transition-colors duration-300"
            >
              <Phone className="w-5 h-5 text-volt" />
              {BUSINESS.phone}
            </a>
            <a
              data-testid="footer-email-link"
              href={BUSINESS.emailHref}
              className="flex items-center gap-3 text-slate-400 hover:text-volt transition-colors duration-300 break-all"
            >
              <Mail className="w-5 h-5 text-volt shrink-0" />
              {BUSINESS.email}
            </a>
          </address>
        </div>

        <div className="md:col-span-2">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-volt mb-5">Hours</p>
          <ul className="space-y-3" data-testid="footer-hours">
            {BUSINESS.hours.map((h) => (
              <li key={h.days} className="text-sm">
                <span className="flex items-center gap-2 text-slate-300">
                  <Clock className="w-3.5 h-3.5 text-volt/70" />
                  {h.days}
                </span>
                <span className="block pl-5 text-slate-500 font-mono text-xs mt-1">{h.time}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-volt mb-5">Services</p>
          <ul className="space-y-2.5">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  data-testid={`footer-service-${s.slug}`}
                  to={`/services/${s.slug}`}
                  className="text-sm text-slate-400 hover:text-volt transition-colors duration-300 inline-flex items-center gap-1.5 group"
                >
                  {s.title}
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-volt mb-5">Service Area</p>
          <ul className="space-y-2.5">
            {LOCATIONS.map((l) => (
              <li key={l.slug}>
                <Link
                  data-testid={`footer-location-${l.slug}`}
                  to={`/locations/${l.slug}`}
                  className="text-sm text-slate-400 hover:text-volt transition-colors duration-300"
                >
                  Smog Check {l.city}, CA
                </Link>
              </li>
            ))}
            <li className="text-sm text-slate-500 pt-2">
              Also serving Castro Valley, San Lorenzo, Fremont & Newark
            </li>
          </ul>
          <a
            data-testid="footer-directions-button"
            href={BUSINESS.mapsDirections}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 bg-cta text-ink rounded-lg font-display font-bold uppercase text-sm px-6 py-3.5 hover:bg-[#E09E00] hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            <MapPin className="w-4 h-4" />
            Get Directions to BaySmog
          </a>
        </div>
      </div>
    </div>

    <div className="px-2 select-none" aria-hidden="true">
      <p className="font-display font-black uppercase leading-[0.8] tracking-tighter text-center text-outline-light text-[16vw] -mb-[2vw]">
        BaySmog
      </p>
    </div>

    <div className="relative w-full border-t border-white/10">
      <div
        className="absolute -top-px left-1/2 -translate-x-1/2 w-1/3 md:w-1/4 h-px bg-gradient-to-r from-transparent via-volt to-transparent opacity-70 blur-[1px]"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-8 max-md:pb-32 flex flex-col md:flex-row items-center justify-between gap-5">
        <p
          data-testid="footer-copyright"
          className="font-mono text-[10px] md:text-xs tracking-[0.2em] text-white/50 uppercase leading-none text-center md:text-left"
        >
          © 2026 Bay Smog Check | All Rights Reserved.
        </p>
        <div className="flex items-center gap-3" data-testid="footer-location">
          <span className="relative flex w-2 h-2" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-volt opacity-60" />
            <span className="relative inline-flex rounded-full w-2 h-2 bg-volt" />
          </span>
          <span className="font-body text-xs md:text-sm text-white/80 font-medium tracking-wide">
            STAR Smog Check · Hayward, CA
          </span>
        </div>
        <a
          data-testid="footer-developer-credit"
          href="https://shineinfosolutions.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Developer website link — Shine InfoSolutions"
          className="group flex items-center px-5 py-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-volt/10 hover:border-volt/30 transition-[background-color,border-color] duration-300"
        >
          <span className="font-body text-xs tracking-wide text-white/75 group-hover:text-volt transition-colors duration-300">
            Designed &amp; Developed by{" "}
            <span className="font-bold text-cta group-hover:text-volt transition-colors duration-300">
              shineinfosolutions.com
            </span>
          </span>
        </a>
      </div>
    </div>
  </footer>
);
